import { useEffect, useMemo, useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { ArrowDownUp, CheckCheck, SlidersHorizontal } from 'lucide-react'
import { NotificationItem } from './NotificationItem'
import type { NotificationDocument } from '@/lib/db/rxdb/schemas/notification'
import type { NotificationListProps } from '../types'
import type { DayBucket, InboxEntry, NotificationTab } from '../utils/notificationInbox'
import { Kbd } from '@/components/ui/kbd'
import { cn } from '@/lib/utils'
import { PhoneMenuButton } from '@/components/app/layout/PhoneMenuButton'
import useLoginStore from '@/store/LoginStore'

type Row =
  | { type: 'header'; key: string; bucket: DayBucket }
  | { type: 'entry'; key: string; entry: InboxEntry<NotificationDocument> }

const TABS: Array<NotificationTab> = ['all', 'mentions', 'assigned']

export function NotificationList({
  groups,
  lookups,
  now,
  tab,
  onTabChange,
  unreadCount,
  onMarkAllRead,
  selectedEntryId,
  expandedEntryIds,
  onSelect,
  onToggleExpand,
  isLoading = false,
  showHints = true,
}: NotificationListProps) {
  const { t } = useTranslation('notifications')
  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)
  const viewportRef = useRef<HTMLDivElement>(null)

  const rows = useMemo<Array<Row>>(
    () =>
      groups.flatMap((group) => [
        { type: 'header' as const, key: `h:${group.bucket}`, bucket: group.bucket },
        ...group.entries.map((entry) => ({ type: 'entry' as const, key: entry.id, entry })),
      ]),
    [groups],
  )

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => viewportRef.current,
    estimateSize: (index) => (rows[index]?.type === 'header' ? 36 : 72),
    getItemKey: (index) => rows[index]?.key ?? index,
    overscan: 6,
  })

  // Keep the selected row visible when it moves via the keyboard.
  useEffect(() => {
    if (!selectedEntryId) return
    const index = rows.findIndex((r) => r.type === 'entry' && r.entry.id === selectedEntryId)
    if (index >= 0) rowVirtualizer.scrollToIndex(index, { align: 'auto' })
    // Scroll only when the selection changes, not on every re-measure.
  }, [selectedEntryId])

  const virtualItems = rowVirtualizer.getVirtualItems()

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="shrink-0 px-4 pb-3 pt-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1">
            <PhoneMenuButton />
            <h1 className="truncate text-[20px] font-bold tracking-[-0.01em]">{t('title')}</h1>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onMarkAllRead}
              disabled={unreadCount === 0}
              className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-[12.5px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
            >
              <CheckCheck className="size-3.5" />
              {t('markAllRead')}
            </button>
            <Link
              to="/$workspaceSlug/settings/notifications"
              params={{ workspaceSlug }}
              aria-label={t('settings')}
              title={t('settings')}
              className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <SlidersHorizontal className="size-3.5" />
            </Link>
          </div>
        </div>
        <div
          role="tablist"
          aria-label={t('tabs.label')}
          className="grid grid-cols-3 gap-0.5 rounded-lg bg-secondary p-0.5"
        >
          {TABS.map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={tab === option}
              onClick={() => onTabChange(option)}
              className={cn(
                'h-7 rounded-md text-[12.5px] font-medium transition-colors',
                tab === option
                  ? 'bg-card text-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t(`tabs.${option}`)}
              {option === 'all' && unreadCount > 0 && (
                <span className="ml-1 tabular-nums text-brand">{unreadCount}</span>
              )}
            </button>
          ))}
        </div>
      </header>

      {isLoading ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">{t('loading')}</p>
      ) : rows.length === 0 ? (
        <p className="px-4 py-8 text-center text-[13px] text-muted-foreground">
          {t(`tabs.empty.${tab}`)}
        </p>
      ) : (
        <div ref={viewportRef} className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          <div
            role="listbox"
            aria-label={t('title')}
            style={{ height: rowVirtualizer.getTotalSize(), width: '100%', position: 'relative' }}
          >
            {virtualItems.map((virtualItem) => {
              const row = rows[virtualItem.index]
              // The virtualizer can momentarily report an index past the end.
              if (!row) return null
              return (
                <div
                  key={row.key}
                  data-index={virtualItem.index}
                  ref={rowVirtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  {row.type === 'header' ? (
                    <h2
                      role="presentation"
                      className="px-3 pb-1.5 pt-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                    >
                      {t(`groups.${row.bucket}`)}
                    </h2>
                  ) : (
                    <div className="pb-1">
                      <NotificationItem
                        entry={row.entry}
                        lookups={lookups}
                        now={now}
                        isSelected={selectedEntryId === row.entry.id}
                        isExpanded={expandedEntryIds.has(row.entry.id)}
                        onSelect={onSelect}
                        onToggleExpand={onToggleExpand}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {showHints && (
        <footer className="flex shrink-0 items-center gap-3 border-t px-4 py-2 text-[11.5px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Kbd className="border bg-card">E</Kbd>
            {t('hints.done')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Kbd className="border bg-card">
              <ArrowDownUp />
            </Kbd>
            {t('hints.move')}
          </span>
        </footer>
      )}
    </div>
  )
}
